import { UiMode } from './uiMode';

enum EventTrackerAvailableSections {
  INTRAFARM = 'intrafarm',
  MULTIFARM = 'multifarm',
  CONNECT = 'connect', // (if user comes from Settings or from Menu, we use the same name)
  NEWS = 'news',
  ACTIVITIES = 'activities',
  FIELDS = 'fields',
  MAP = 'map',
  DASHBOARD = 'dashboard',
  WAREHOUSES = 'warehouses',
  PRODUCTS = 'products',
  LOADS = 'loads',
  SILOS = 'silos',
  COMMUNICATIONS = 'communications',
  WEATHER = 'weather',
  SENSORS = 'sensors',
  NUTRITION = 'nutrition',
  IRRIGATION = 'irrigation',
  AUTOMATIC_IRRIGATION = 'automatic_irrigation',
  PATHOGENS = 'pathogens',
  INSECTS = 'insects',
  DISEASE_RECOGNITION = 'disease_recognition',
  NOTES = 'notes',
  ALERTS = 'alerts', // (if user comes from Top Menu or from the Management section of the Menu, we use the same name)
  FINANCES = 'finances',
  OPERATIONAL_MANAGEMENT = 'operational_management',
  TASK_PLAN = 'task_plan',
  SUSTAINABILITY = 'sustainability',
  CHECKS = 'checks',
  MACHINERY = 'machinery',
  INCAB_CLOUD = 'incab_cloud',
  INCAB = 'incab',
  SATELLITE = 'satellite',
  PRESCRIPTION_MAPS = 'prescription_maps',
  ANALYSIS = 'analysis',
  TASK_VIEWER = 'task_viewer',
  INSURANCE = 'insurance',
  CONTRACTS = 'contracts',
  MY_FARM = 'my_farm',
  SCAN = 'scan',
  SETTINGS = 'settings',
  EXPORT_DOCUMENTS = 'export_documents',
  BARNS = 'barns',
  ANIMALS = 'animals',
  RATIONS = 'rations',
  SURVEY = 'survey',
  PRODUCTION_BATCHES = 'production_batches',
  CROP_PLANNING = 'crop_planning',
  MENU = 'menu',
  // FOLLOWING ARE NOT SECTIONS, BUT NEEDED FOR SPECIAL EVENTS
  LOGIN = 'login',
  OPEN_APPLICATION_WEB = 'open_application_web',
  CLOSE_APPLICATION_WEB = 'close_application_web',
  LOGOUT = 'logout',
}
export type EventTrackerAvailableSection = `${EventTrackerAvailableSections}`;

type ModuleUrl = string;

//* Modules events. Key is the url after 'auth/' and value is the section name
// If the module is not listed here, it won't be tracked
// Note: some modules have subroutes, for example 'company-data/connections' or 'satellite/manage-prescription'
// In that case, we need to list the full path after 'auth/' to be able to track it correctly
const farmModulesUrlMap: Record<ModuleUrl, EventTrackerAvailableSection> = {
  activities: 'activities',
  alerts: 'alerts',
  satellite: 'satellite',
  checks: 'checks',
  training: 'communications',
  connect: 'connect',
  'company-data/connections': 'connect',
  'contract-management': 'contracts',
  'crop-planning': 'crop_planning',
  dashboard: 'dashboard',
  'import-export': 'export_documents',
  financials: 'finances',
  fields: 'fields',
  insects: 'insects',
  irrigation: 'irrigation',
  loads: 'loads',
  equipments: 'machinery',
  map: 'map',
  news: 'news',
  notes: 'notes',
  nutrition: 'nutrition',
  'operation-management': 'operational_management',
  diseases: 'pathogens',
  batchs: 'production_batches',
  products: 'products',
  sensors: 'sensors',
  silos: 'silos',
  sustainability: 'sustainability',
  'task-viewer': 'task_viewer',
  'task-plan': 'task_plan',
  survey: 'survey',
  containers: 'warehouses',
  weather: 'weather',
  'satellite/manage-files': 'analysis',
  'satellite/manage-prescription': 'prescription_maps',
};
const livestockModulesUrlMap: Record<ModuleUrl, EventTrackerAvailableSection> = {
  'stable-activity': 'activities',
  alerts: 'alerts',
  animals: 'animals',
  'stable-loads': 'loads',
  stables: 'barns',
  training: 'communications',
  connect: 'connect',
  'company-data/connections': 'connect',
  dashboard: 'dashboard',
  'import-export': 'export_documents',
  equipments: 'machinery',
  map: 'map',
  news: 'news',
  notes: 'notes',
  products: 'products',
  rations: 'rations',
  sensors: 'sensors',
  silos: 'silos',
  survey: 'survey',
  'task-viewer': 'task_viewer',
  containers: 'warehouses',
  weather: 'weather',
  'contract-management': 'contracts',
  'satellite/manage-files': 'analysis',
  'satellite/manage-prescription': 'prescription_maps',
};

export const EventTrackerModulesMapper: Record<
  UiMode,
  Record<ModuleUrl, EventTrackerAvailableSection>
> = {
  FARM: farmModulesUrlMap,
  LIVESTOCK: livestockModulesUrlMap,
};

// Click/static events
export enum EventTracker {
  CLICK_LIVESTOCK_DASHBOARD_CREATE_ACTIVITY = 'click_livestock_dashboard_create_activity',
  CLICK_LIVESTOCK_DASHBOARD_MAP = 'click_livestock_dashboard_map',
  CLICK_AGRO_DASHBOARD_MAP = 'click_agro_dashboard_map',
  CLICK_AGRO_DASHBOARD_CREATE_WAREHOUSE_MOVEMENT = 'click_agro_dashboard_create_warehouse_movement',
  CLICK_AGRO_DASHBOARD_CREATE_ACTIVITY = 'click_agro_dashboard_create_activity',
  CLICK_MENU_TUTORIAL = 'click_menu_tutorial',
  CLICK_TOP_BAR_ACCOUNT_COMPANY = 'click_top_bar_account_company',
  CLICK_MENU_SETTINGS = 'click_menu_settings',
  CLICK_TOP_BAR_ACCOUNT = 'click_top_bar_account',
  CLICK_TOP_BAR_LANGUAGE = 'click_top_bar_language',
  CLICK_TOP_BAR_NOTIFICATION_CENTER = 'click_top_bar_notification_center',
  CLICK_TOP_BAR_RELATED_COMPANIES = 'click_top_bar_related_companies',
  CLICK_MENU_EXPORT_DOCUMENTS = 'click_menu_export_documents',
  CLICK_TOP_MENU_STORE = 'click_top_menu_store',
  CLICK_MENU_COMMUNICATIONS = 'click_menu_communications',
  CLICK_LIVESTOCK_MENU_RATIONS = 'click_livestock_menu_rations',
  CLICK_LIVESTOCK_MENU_SILOS = 'click_livestock_menu_silos',
  CLICK_LIVESTOCK_MENU_LOADS = 'click_livestock_menu_loads',
  CLICK_LIVESTOCK_MENU_DASHBOARD = 'click_livestock_menu_dashboard',
  CLICK_LIVESTOCK_MENU_MAP = 'click_livestock_menu_map',
  CLICK_LIVESTOCK_MENU_ACTIVITIES = 'click_livestock_menu_activities',
  CLICK_LIVESTOCK_MENU_ANIMALS = 'click_livestock_menu_animals',
  CLICK_LIVESTOCK_MENU_BARNS = 'click_livestock_menu_barns',
  CLICK_MENU_TASK_VIEWER = 'click_menu_task_viewer',
  CLICK_AGRO_MENU_ANALYSIS = 'click_agro_menu_analysis',
  CLICK_AGRO_MENU_SATELLITE = 'click_agro_menu_satellite',
  CLICK_MENU_MACHINERY = 'click_menu_machinery',
  CLICK_AGRO_MENU_CHECKS = 'click_agro_menu_checks',
  CLICK_AGRO_MENU_SUSTAINABILITY = 'click_agro_menu_sustainability',
  CLICK_AGRO_MENU_TASK_PLAN = 'click_agro_menu_task_plan',
  CLICK_AGRO_MENU_OPERATION_MANAGEMENT = 'click_agro_menu_operation_management',
  CLICK_AGRO_MENU_PRODUCTION_BATCHES = 'click_agro_menu_production_batches',
  CLICK_AGRO_MENU_CROP_PLANNING = 'click_agro_menu_crop_planning',
  CLICK_AGRO_MENU_FINANCES = 'click_agro_menu_finances',
  CLICK_MENU_ALERTS = 'click_menu_alerts',
  CLICK_MENU_NOTES = 'click_menu_notes',
  CLICK_AGRO_MENU_INSECTS = 'click_agro_menu_insects',
  CLICK_AGRO_MENU_PATHOGENS = 'click_agro_menu_pathogens',
  CLICK_AGRO_MENU_IRRIGATION = 'click_agro_menu_irrigation',
  CLICK_AGRO_MENU_NUTRITION = 'click_agro_menu_nutrition',
  CLICK_MENU_SENSORS = 'click_menu_sensors',
  CLICK_MENU_WEATHER = 'click_menu_weather',
  CLICK_AGRO_MENU_SILOS = 'click_agro_menu_silos',
  CLICK_AGRO_MENU_LOADS = 'click_agro_menu_loads',
  CLICK_AGRO_MENU_PRODUCTS = 'click_agro_menu_products',
  CLICK_AGRO_MENU_WAREHOUSES = 'click_agro_menu_warehouses',
  CLICK_AGRO_MENU_ACTIVITIES = 'click_agro_menu_activities',
  CLICK_AGRO_MENU_MAP = 'click_agro_menu_map',
  CLICK_AGRO_MENU_FIELDS = 'click_agro_menu_fields',
  CLICK_AGRO_MENU_DASHBOARD = 'click_agro_menu_dashboard',
  CLICK_TOP_MENU_ALERTS = 'click_top_menu_alerts',
  CLICK_TOP_MENU_CONNECT = 'click_top_menu_connect',
  CLICK_TOP_MENU_NEWS = 'click_top_menu_news',
  CLICK_TOP_MENU_SUPPORT = 'click_top_menu_support',
  CLICK_LIVESTOCK_DASHBOARD_CRATE_SILOS_MOVEMENT = 'click_livestock_dashboard_create_silos_movement',
  CLICK_AGRO_PRESCRIPTION_MAPS = 'click_agro_prescription_maps',
  CLICK_MODULE_AGRO_INCAB = 'click_module_agro_incab',
  CLICK_MENU_AGRO_CONTRACTS = 'click_menu_agro_contracts',
  CLICK_SETTINGS_CONNECT = 'click_settings_connect',
  LOGIN = 'login',
  OPEN_APPLICATION_WEB = 'open_application_web',
  CLOSE_APPLICATION_WEB = 'close_application_web',
  LOGOUT = 'logout',
  SELECT_INTRAFARM = 'select_intrafarm',
  SELECT_MULTIFARM = 'select_multifarm',
  CLICK_LIVESTOCK_MENU_WAREHOUSES = 'click_livestock_menu_warehouses',
  CLICK_LIVESTOCK_MENU_PRODUCTS = 'click_livestock_menu_products',
}

// Mapping click/static events to sections
export const EventTrackerClickMapper: Record<EventTracker, EventTrackerAvailableSection> = {
  [EventTracker.CLICK_LIVESTOCK_DASHBOARD_CREATE_ACTIVITY]: 'activities',
  [EventTracker.CLICK_LIVESTOCK_DASHBOARD_MAP]: 'map',
  [EventTracker.CLICK_AGRO_DASHBOARD_MAP]: 'map',
  [EventTracker.CLICK_AGRO_DASHBOARD_CREATE_WAREHOUSE_MOVEMENT]: 'warehouses',
  [EventTracker.CLICK_AGRO_DASHBOARD_CREATE_ACTIVITY]: 'activities',
  [EventTracker.CLICK_MENU_TUTORIAL]: 'menu',
  [EventTracker.CLICK_TOP_BAR_ACCOUNT_COMPANY]: 'my_farm',
  [EventTracker.CLICK_MENU_SETTINGS]: 'settings',
  [EventTracker.CLICK_TOP_BAR_ACCOUNT]: 'menu',
  [EventTracker.CLICK_TOP_BAR_LANGUAGE]: 'menu',
  [EventTracker.CLICK_TOP_BAR_NOTIFICATION_CENTER]: 'menu',
  [EventTracker.CLICK_TOP_BAR_RELATED_COMPANIES]: 'menu',
  [EventTracker.CLICK_MENU_EXPORT_DOCUMENTS]: 'export_documents',
  [EventTracker.CLICK_TOP_MENU_STORE]: 'menu',
  [EventTracker.CLICK_MENU_COMMUNICATIONS]: 'communications',
  [EventTracker.CLICK_LIVESTOCK_MENU_RATIONS]: 'rations',
  [EventTracker.CLICK_LIVESTOCK_MENU_SILOS]: 'silos',
  [EventTracker.CLICK_LIVESTOCK_MENU_LOADS]: 'loads',
  [EventTracker.CLICK_LIVESTOCK_MENU_DASHBOARD]: 'dashboard',
  [EventTracker.CLICK_LIVESTOCK_MENU_MAP]: 'map',
  [EventTracker.CLICK_LIVESTOCK_MENU_ACTIVITIES]: 'activities',
  [EventTracker.CLICK_LIVESTOCK_MENU_ANIMALS]: 'animals',
  [EventTracker.CLICK_LIVESTOCK_MENU_BARNS]: 'barns',
  [EventTracker.CLICK_MENU_TASK_VIEWER]: 'task_viewer',
  [EventTracker.CLICK_AGRO_MENU_ANALYSIS]: 'analysis',
  [EventTracker.CLICK_AGRO_MENU_SATELLITE]: 'satellite',
  [EventTracker.CLICK_MENU_MACHINERY]: 'machinery',
  [EventTracker.CLICK_AGRO_MENU_CHECKS]: 'checks',
  [EventTracker.CLICK_AGRO_MENU_SUSTAINABILITY]: 'sustainability',
  [EventTracker.CLICK_AGRO_MENU_TASK_PLAN]: 'task_plan',
  [EventTracker.CLICK_AGRO_MENU_OPERATION_MANAGEMENT]: 'operational_management',
  [EventTracker.CLICK_AGRO_MENU_PRODUCTION_BATCHES]: 'production_batches',
  [EventTracker.CLICK_AGRO_MENU_CROP_PLANNING]: 'crop_planning',
  [EventTracker.CLICK_AGRO_MENU_FINANCES]: 'finances',
  [EventTracker.CLICK_MENU_ALERTS]: 'alerts',
  [EventTracker.CLICK_MENU_NOTES]: 'notes',
  [EventTracker.CLICK_AGRO_MENU_INSECTS]: 'insects',
  [EventTracker.CLICK_AGRO_MENU_PATHOGENS]: 'pathogens',
  [EventTracker.CLICK_AGRO_MENU_IRRIGATION]: 'irrigation',
  [EventTracker.CLICK_AGRO_MENU_NUTRITION]: 'nutrition',
  [EventTracker.CLICK_MENU_SENSORS]: 'sensors',
  [EventTracker.CLICK_MENU_WEATHER]: 'weather',
  [EventTracker.CLICK_AGRO_MENU_SILOS]: 'silos',
  [EventTracker.CLICK_AGRO_MENU_LOADS]: 'loads',
  [EventTracker.CLICK_AGRO_MENU_PRODUCTS]: 'products',
  [EventTracker.CLICK_AGRO_MENU_WAREHOUSES]: 'warehouses',
  [EventTracker.CLICK_AGRO_MENU_ACTIVITIES]: 'activities',
  [EventTracker.CLICK_AGRO_MENU_MAP]: 'map',
  [EventTracker.CLICK_AGRO_MENU_FIELDS]: 'fields',
  [EventTracker.CLICK_AGRO_MENU_DASHBOARD]: 'dashboard',
  [EventTracker.CLICK_TOP_MENU_ALERTS]: 'alerts',
  [EventTracker.CLICK_TOP_MENU_CONNECT]: 'connect',
  [EventTracker.CLICK_TOP_MENU_NEWS]: 'news',
  [EventTracker.CLICK_TOP_MENU_SUPPORT]: 'menu',
  [EventTracker.CLICK_LIVESTOCK_DASHBOARD_CRATE_SILOS_MOVEMENT]: 'silos',
  [EventTracker.CLICK_AGRO_PRESCRIPTION_MAPS]: 'prescription_maps',
  [EventTracker.CLICK_MODULE_AGRO_INCAB]: 'incab',
  [EventTracker.CLICK_MENU_AGRO_CONTRACTS]: 'contracts',
  [EventTracker.CLICK_SETTINGS_CONNECT]: 'connect',
  [EventTracker.SELECT_INTRAFARM]: 'intrafarm',
  [EventTracker.SELECT_MULTIFARM]: 'multifarm',
  [EventTracker.LOGIN]: 'login',
  [EventTracker.OPEN_APPLICATION_WEB]: 'open_application_web',
  [EventTracker.CLOSE_APPLICATION_WEB]: 'close_application_web',
  [EventTracker.LOGOUT]: 'logout',
  [EventTracker.CLICK_LIVESTOCK_MENU_WAREHOUSES]: 'warehouses',
  [EventTracker.CLICK_LIVESTOCK_MENU_PRODUCTS]: 'products',
};
